import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiSearch, FiPlusCircle, FiXCircle, FiPlus, FiMinus, FiStar, FiZap, FiChevronLeft, FiChevronRight, FiPause, FiPlay, FiShoppingCart, FiPrinter, FiFileText, FiMic, FiTag, FiShare2, FiCreditCard, FiPercent, FiGift, FiUser, FiWifiOff, FiPackage, FiX, FiCamera } from 'react-icons/fi';
import ClientCreditModal from '../components/ClientCreditModal';
import ReceiptModal from '../components/ReceiptModal'; // Importer le nouveau composant
import InvoiceModal from '../components/InvoiceModal'; // Importer le nouveau composant
import { toast } from 'react-toastify';
import api from '../services/api';
import AuthContext from '../context/AuthContext';
import DashboardContext from '../context/DashboardContext';
import Spinner from '../components/Spinner';
import { Html5Qrcode } from "html5-qrcode";
import html2canvas from 'html2canvas';

const ITEMS_PER_PAGE = 12; // 4 colonnes * 3 lignes

// --- Fonctions utilitaires pour la recherche floue ---
const normalizeString = (str) => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
};

const levenshteinDistance = (a, b) => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      const cost = b.charAt(i - 1) === a.charAt(j - 1) ? 0 : 1;
      matrix[i][j] = Math.min(matrix[i - 1][j - 1] + cost, Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1));
    }
  }
  return matrix[b.length][a.length];
};
// ----------------------------------------------------

const CheckoutModal = ({ isOpen, onClose, total, onConfirm, client }) => {
  const [amount, setAmount] = useState('');
  const [discount, setDiscount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Espèces');
  const [useLoyalty, setUseLoyalty] = useState(false);

  // 1 point = 10 FCFA de réduction (exemple)
  const loyaltyPoints = client?.loyaltyPoints || Math.floor((client?.totalSpent || 0) / 1000) || 0;
  const loyaltyDiscount = useLoyalty ? Math.min(loyaltyPoints * 10, total) : 0;

  const finalTotal = Math.max(0, total - (parseFloat(discount) || 0) - loyaltyDiscount);
  const change = amount ? parseFloat(amount) - finalTotal : 0;

  useEffect(() => {
    if (isOpen) {
        setAmount('');
        setDiscount('');
        setPaymentMethod('Espèces');
        setUseLoyalty(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
        <h3 className="text-xl font-bold mb-4 text-gray-800">Encaissement</h3>
        <div className="mb-4 text-center bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-500 text-sm uppercase font-bold">Total à payer</p>
            <p className="text-3xl font-extrabold text-brand-blue">{finalTotal.toLocaleString('fr-FR')} F</p>
            {(discount > 0 || useLoyalty) && (
              <p className="text-xs text-green-600 line-through">{total.toLocaleString('fr-FR')} F</p>
            )}
        </div>
        
        <div className="mb-6">
            <div className="flex gap-2 mb-4">
                <div className="flex-1">
                    <label className="block text-xs font-bold text-gray-500 mb-1">Remise (CFA)</label>
                    <div className="relative">
                        <FiPercent className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" size={14}/>
                        <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-full p-2 pl-8 border rounded-lg text-sm" placeholder="0" />
                    </div>
                </div>
                <div className="flex-1">
                    <label className="block text-xs font-bold text-gray-500 mb-1">Mode de paiement</label>
                    <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full p-2 border rounded-lg text-sm bg-white">
                        <option value="Espèces">Espèces 💵</option>
                        <option value="Wave">Wave 🔵</option>
                        <option value="Orange Money">Orange Money 🟠</option>
                        <option value="Carte Bancaire">Carte 💳</option>
                        <option value="Virement">Virement 🏦</option>
                    </select>
                </div>
            </div>

            {client && loyaltyPoints > 0 && (
              <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-100 flex items-center justify-between">
                <div className="flex items-center text-purple-800 text-sm font-bold">
                  <FiGift className="mr-2" />
                  <span>{loyaltyPoints} pts disponibles (-{loyaltyPoints * 10} F)</span>
                </div>
                <input type="checkbox" checked={useLoyalty} onChange={(e) => setUseLoyalty(e.target.checked)} className="h-5 w-5 text-purple-600 rounded focus:ring-purple-500" />
              </div>
            )}

            <label className="block text-sm font-bold text-gray-700 mb-2">Montant Reçu</label>
            <input 
                type="number" 
                value={amount} 
                onChange={(e) => setAmount(e.target.value)} 
                className="w-full p-3 border-2 border-gray-300 rounded-lg text-lg font-bold focus:border-brand-green focus:outline-none transition-colors"
                placeholder="0" 
                autoFocus
            />
        </div>

        {amount !== '' && (
            <div className={`mb-6 p-3 rounded-lg text-center border ${change >= 0 ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
                <p className="text-xs font-bold uppercase mb-1">Monnaie à rendre</p>
                <p className="text-2xl font-bold">{change >= 0 ? `${change.toLocaleString('fr-FR')} F` : 'Montant insuffisant'}</p>
            </div>
        )}

        <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-3 rounded-lg transition-colors">Annuler</button>
            <button onClick={() => onConfirm({
                amountReceived: parseFloat(amount),
                manualDiscount: parseFloat(discount) || 0,
                loyaltyDiscount: loyaltyDiscount,
                paymentMethod: paymentMethod,
                pointsUsed: useLoyalty ? (loyaltyDiscount / 10) : 0
            })} disabled={!amount || change < 0} className="flex-1 bg-brand-green hover:bg-green-600 text-white font-bold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md">Valider</button>
        </div>
      </div>
    </div>
  );
};

const MiscItemModal = ({ isOpen, onClose, onConfirm }) => {
  const [name, setName] = useState('Divers');
  const [price, setPrice] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
        <h3 className="text-xl font-bold mb-4 text-gray-800">Vente Libre / Divers</h3>
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Libellé</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full p-2 border rounded-lg" placeholder="Ex: Service, Transport..." />
            </div>
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Prix (CFA)</label>
                <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="w-full p-2 border rounded-lg" placeholder="0" autoFocus />
            </div>
        </div>
        <div className="flex gap-3 mt-6">
            <button onClick={onClose} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 rounded-lg">Annuler</button>
            <button onClick={() => onConfirm(name, parseFloat(price))} disabled={!price} className="flex-1 bg-brand-blue hover:bg-blue-600 text-white font-bold py-2 rounded-lg disabled:opacity-50">Ajouter</button>
        </div>
      </div>
    </div>
  );
};

const VariantSelectionModal = ({ isOpen, onClose, product, onSelectVariant }) => {
  if (!isOpen || !product || !product.variants || product.variants.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Choisir une variante</h3>
          <button onClick={onClose}><FiX size={24} /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">Produit : <span className="font-bold">{product.name}</span></p>
        <div className="grid grid-cols-3 gap-3">
          {product.variants.map(variant => (
            <button 
              key={variant.name}
              onClick={() => onSelectVariant(variant)}
              disabled={variant.quantity <= 0}
              className="p-4 border rounded-lg text-center font-bold hover:border-brand-blue hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            >
              <span className="block">{variant.name}</span>
              <span className="text-xs text-gray-400">({variant.quantity})</span>
            </button>
          ))}
        </div>
        <div className="mt-6 text-right">
          <button onClick={onClose} className="bg-gray-200 text-gray-800 font-bold py-2 px-4 rounded-lg">Annuler</button>
        </div>
      </div>
    </div>
  );
};

const ScannerModal = ({ isOpen, onClose, onScan }) => {
  const [isContinuous, setIsContinuous] = useState(false);
  const lastScannedCode = useRef(null);
  const lastScannedTime = useRef(0);
  const [isTorchOn, setIsTorchOn] = useState(false); // État pour la lampe de poche
  const html5QrCodeRef = useRef(null); // Référence pour l'instance Html5Qrcode

  const playFeedback = () => {
    // Vibration (100ms)
    if (navigator.vibrate) navigator.vibrate(100);
    
    // Génération d'un bip sonore via Web Audio API
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, audioCtx.currentTime); // Note La (A5)
      gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.1);
    } catch (e) {
      console.warn("Le feedback audio n'a pas pu être joué", e);
    }
  };

  useEffect(() => {
    let html5QrCode;
    if (isOpen) {
      lastScannedCode.current = null;
      lastScannedTime.current = 0;
      html5QrCode = new Html5Qrcode("barcode-reader");
      html5QrCodeRef.current = html5QrCode; // Stocker l'instance
      const config = { fps: 10, qrbox: { width: 250, height: 150 } };

      html5QrCode.start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          const now = Date.now();
          // Anti-rebond : ignore si c'est le même code scanné il y a moins de 2.5 secondes
          if (isContinuous && decodedText === lastScannedCode.current && (now - lastScannedTime.current) < 2500) {
            return;
          }
          lastScannedCode.current = decodedText;
          lastScannedTime.current = now;
          playFeedback();
          onScan(decodedText, isContinuous);
          if (!isContinuous) {
            html5QrCode.stop().catch(() => {});
          }
        }
      ).catch(err => {
        toast.error("Erreur d'accès à la caméra.");
        onClose();
      });
    }

    return () => {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        html5QrCodeRef.current.stop().catch(() => {});
        // S'assurer que la lampe de poche est éteinte à la fermeture
        if (isTorchOn) {
          html5QrCodeRef.current.setTorchOff().catch(() => {});
          setIsTorchOn(false);
        }
      }
    };
  }, [isOpen, onScan, onClose, isContinuous, isTorchOn]); // Ajouter isTorchOn aux dépendances

  // Fonction pour activer/désactiver la lampe de poche
  const toggleTorch = useCallback(async () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      try {
        if (isTorchOn) {
          await html5QrCodeRef.current.setTorchOff();
        } else {
          await html5QrCodeRef.current.setTorchOn();
        }
        setIsTorchOn(prev => !prev);
      } catch (err) {
        toast.error("Impossible d'activer/désactiver la lampe de poche.");
        console.error("Torch error:", err);
      }
    }
  }, [isTorchOn]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 z-[100] flex flex-col items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden relative shadow-2xl">
        <button onClick={onClose} className="absolute top-4 right-4 z-10 bg-white/80 p-2 rounded-full text-gray-800"><FiX size={20}/></button>
        <div className="p-6 text-center border-b">
          <h3 className="text-xl font-bold text-gray-800">Scanner un produit</h3>
          <div className="mt-2 flex items-center justify-center gap-2">
            <input 
              type="checkbox" 
              id="continuous-scan" 
              checked={isContinuous} 
              onChange={(e) => setIsContinuous(e.target.checked)}
              className="w-4 h-4 text-brand-blue rounded border-gray-300 focus:ring-brand-blue cursor-pointer"
            />
            <label htmlFor="continuous-scan" className="text-sm font-semibold text-gray-600 cursor-pointer">Scan continu</label>
            {/* Bouton Lampe de poche */}
            <button
              onClick={toggleTorch}
              className={`p-2 rounded-full transition-colors ${isTorchOn ? 'bg-yellow-100 text-yellow-600' : 'text-gray-400 hover:text-yellow-600 hover:bg-gray-100'}`}
              title="Activer/Désactiver la lampe de poche"
            >
              <FiZap size={20} />
            </button>
          </div>
        </div>
        <div id="barcode-reader" className="w-full bg-black"></div>
        <div className="p-6 text-center text-sm text-gray-500 font-medium">Placez le code-barres du produit devant la caméra.</div>
      </div>
    </div>
  );
};

// Fonction utilitaire pour générer une couleur pastel basée sur le nom du produit
const getProductColor = (name) => {
  const colors = ['bg-red-100 text-red-600', 'bg-orange-100 text-orange-600', 'bg-amber-100 text-amber-600', 'bg-yellow-100 text-yellow-600', 'bg-lime-100 text-lime-600', 'bg-green-100 text-green-600', 'bg-emerald-100 text-emerald-600', 'bg-teal-100 text-teal-600', 'bg-cyan-100 text-cyan-600', 'bg-sky-100 text-sky-600', 'bg-blue-100 text-blue-600', 'bg-indigo-100 text-indigo-600', 'bg-violet-100 text-violet-600', 'bg-purple-100 text-purple-600', 'bg-fuchsia-100 text-fuchsia-600', 'bg-pink-100 text-pink-600', 'bg-rose-100 text-rose-600'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const Sales = () => {
  const [allProducts, setAllProducts] = useState([]); // Cache pour tous les produits
  const [topProducts, setTopProducts] = useState([]); // Pour l'accès rapide
  const searchInputRef = useRef(null);
  const clientDropdownRef = useRef(null);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(''); // Nouveau state pour le client sélectionné
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [searchTerm, setSearchTerm] = useState('');
  const [cartItems, setCartItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [heldSales, setHeldSales] = useState(() => {
    try {
      const saved = localStorage.getItem('heldSales');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      return [];
    }
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false); // État pour le ticket
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false); // État pour la calculatrice
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false); // État pour la facture
  const [completedSale, setCompletedSale] = useState(null); // État pour stocker les infos de la vente
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const [crossSellSuggestions, setCrossSellSuggestions] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [isMiscModalOpen, setIsMiscModalOpen] = useState(false);
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
  const [productForVariantSelection, setProductForVariantSelection] = useState(null);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const { user, openUpgradeModal } = useContext(AuthContext);
  const { refreshDashboard } = useContext(DashboardContext);
  const productGridRef = useRef(null);

  const addToCart = useCallback((product, quantity = 1) => {
    if (!product.isService && product.quantity <= 0) {
      toast.error(`"${product.name}" est en rupture de stock.`);
      return;
    }

    setCartItems(prevItems => {
        const existingItem = prevItems.find(item => item._id === product._id);
        if (existingItem) {
            if (!product.isService && (existingItem.quantity + quantity) > product.quantity) {
                toast.warn(`Stock maximum atteint pour "${product.name}".`);
                return prevItems;
            }
            return prevItems.map(item => item._id === product._id ? { ...item, quantity: item.quantity + quantity } : item);
        }
        return [...prevItems, { ...product, quantity }];
    });
  }, []);

  const handleAddToCart = useCallback((product) => {
    // Si le produit a des variantes, on ouvre le modal de sélection
    if (product.variants && product.variants.length > 0) {
      setProductForVariantSelection(product);
      setIsVariantModalOpen(true);
    } else {
      // Sinon, on l'ajoute directement
      addToCart(product);
    }
  }, [addToCart]);

  const handleBarcodeScan = useCallback((barcode, isContinuous) => {
    const product = allProducts.find(p => p.barcode === barcode);
    if (product) {
      // Si le produit a des variantes, on ferme forcément le scanner pour laisser l'utilisateur choisir
      if (product.variants && product.variants.length > 0) {
        setIsScannerOpen(false);
        handleAddToCart(product);
        return;
      }
      handleAddToCart(product);
      toast.success(`${product.name} ajouté !`, { autoClose: 1000 });
      if (!isContinuous) {
        setIsScannerOpen(false);
      }
    } else {
      toast.error(`Code-barres "${barcode}" non reconnu.`);
    }
  }, [allProducts, handleAddToCart]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      // 1. D'ABORD : Charger depuis le cache local (pour un affichage immédiat même hors ligne)
      const cachedProducts = localStorage.getItem('products_cache');
      const cachedClients = localStorage.getItem('clients_cache');
      const cachedTopProducts = localStorage.getItem('top_products_cache');

      if (cachedProducts) setAllProducts(JSON.parse(cachedProducts));
      if (cachedClients) setClients(JSON.parse(cachedClients));
      if (cachedTopProducts) setTopProducts(JSON.parse(cachedTopProducts));

      // 2. ENSUITE : Si connecté, rafraîchir les données et mettre à jour le cache
      if (navigator.onLine) {
        try {
          const [clientsResponse, initialProductsResponse] = await Promise.all([
            api.get('/clients'),
            api.get('/products', { params: { limit: 8, sort: 'bestseller' } })
          ]);
          
          setClients(clientsResponse.data);
          setTopProducts(initialProductsResponse.data);
          
          // Mise à jour du cache partiel
          localStorage.setItem('clients_cache', JSON.stringify(clientsResponse.data));
          localStorage.setItem('top_products_cache', JSON.stringify(initialProductsResponse.data));

          // Si le cache complet n'existait pas encore, on affiche au moins les top produits
          if (!cachedProducts) {
             setAllProducts(initialProductsResponse.data);
          }

          // Récupération silencieuse de tout le catalogue pour le cache
          const allProductsResponse = await api.get('/products');
          if (allProductsResponse.data) {
            setAllProducts(allProductsResponse.data);
            localStorage.setItem('products_cache', JSON.stringify(allProductsResponse.data));
          }
        } catch (error) {
          console.error("Erreur de mise à jour des données:", error);
          // On ne bloque pas si on a des données en cache
          if (!cachedProducts) toast.error("Impossible de charger les produits.");
        }
      }
      
      setLoading(false);
    };

    loadData();

  }, []);

  // Gestion du statut en ligne/hors-ligne et synchronisation
  useEffect(() => {
    const handleStatusChange = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', handleStatusChange);
    window.addEventListener('offline', handleStatusChange);

    const syncOfflineSales = async () => {
      if (navigator.onLine) {
        const queue = JSON.parse(localStorage.getItem('offline_sales_queue') || '[]');
        if (queue.length > 0) {
          const toastId = toast.info("Connexion rétablie. Synchronisation des ventes...", { autoClose: false });
          const remaining = [];
          let syncedCount = 0;

          for (const sale of queue) {
            try {
              await api.post('/sales', sale);
              syncedCount++;
            } catch (error) {
              console.error("Sync failed for sale", error);
              remaining.push(sale);
            }
          }

          localStorage.setItem('offline_sales_queue', JSON.stringify(remaining));
          toast.dismiss(toastId);

          if (syncedCount > 0) {
            toast.success(`${syncedCount} ventes hors-ligne synchronisées !`);
            refreshDashboard();
          }
        }
      }
    };

    window.addEventListener('online', syncOfflineSales);
    // Tentative de sync au montage si déjà en ligne
    if (navigator.onLine) syncOfflineSales();

    return () => {
      window.removeEventListener('online', handleStatusChange);
      window.removeEventListener('offline', handleStatusChange);
      window.removeEventListener('online', syncOfflineSales);
    };
  }, [refreshDashboard]);

  // Persistance des ventes en attente dans le localStorage
  useEffect(() => {
    localStorage.setItem('heldSales', JSON.stringify(heldSales));
  }, [heldSales]);

  useEffect(() => {
    // Met le focus sur le champ de recherche au chargement de la page
    searchInputRef.current?.focus();
  }, [loading]);

  // Fermer le dropdown client si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target)) {
        setIsClientDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const newTotal = cartItems.reduce((acc, item) => acc + item.salePrice * item.quantity, 0);
    setTotal(newTotal);
  }, [cartItems]);

  // Réinitialiser la page quand on cherche
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Simule la récupération de suggestions de vente croisée
  useEffect(() => {
    const fetchCrossSellSuggestions = async () => {
      if (cartItems.length === 0) {
        setCrossSellSuggestions([]);
        return;
      }
      try {
        // Idéalement, cet appel enverrait les IDs des produits dans le panier
        // et le backend renverrait des suggestions pertinentes.
        // const response = await api.post('/products/cross-sell-suggestions', { productIds: cartItems.map(p => p._id) });
        // setCrossSellSuggestions(response.data);
      } catch (error) {
        // Ne rien faire en cas d'erreur pour ne pas gêner l'utilisateur
      }
    };
    fetchCrossSellSuggestions();
  }, [cartItems]);

  const handleVoiceSearch = () => {
    if (!('webkitSpeechRecognition' in window)) {
      toast.error("La recherche vocale n'est pas supportée par ce navigateur.");
      return;
    }
    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setSearchTerm(transcript);
    };
    recognition.start();
  };

  const handleSelectVariant = (variant) => {
    const productWithVariant = {
      ...productForVariantSelection,
      _id: `${productForVariantSelection._id}-${variant.name}`, // ID unique pour la variante
      name: `${productForVariantSelection.name} (${variant.name})`,
      quantity: variant.quantity, // Stock de la variante
      variantName: variant.name,
      originalProductId: productForVariantSelection._id
    };
    addToCart(productWithVariant);
    setIsVariantModalOpen(false);
  };

  const addMiscItemToCart = (name, price) => {
    const product = { _id: `misc-${Date.now()}`, name, salePrice: price, quantity: 1, isMisc: true };
    setCartItems(prevItems => [...prevItems, product]);
    setIsMiscModalOpen(false);
    toast.success("Article ajouté !");
  };

  const updateQuantity = (productId, amount) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item._id === productId);
      if (!existingItem) return prevItems;

      // Pour les variantes, on cherche le stock dans le produit original
      const originalProduct = existingItem.originalProductId ? allProducts.find(p => p._id === existingItem.originalProductId) : allProducts.find(p => p._id === productId);
      const variant = originalProduct?.variants?.find(v => v.name === existingItem.variantName);
      const stockLimit = variant ? variant.quantity : originalProduct?.quantity;

      if (originalProduct && !originalProduct.isService && amount > 0 && (existingItem.quantity + amount) > stockLimit) {
        toast.warn(`Stock insuffisant pour "${existingItem.name}". Disponible : ${stockLimit}`);
        return prevItems;
      }

      return prevItems
        .map(item => item._id === productId ? { ...item, quantity: Math.max(0, item.quantity + amount) } : item)
        .filter(item => item.quantity > 0); // Supprime l'article si la quantité est 0
    });
  };

  const removeFromCart = (productId) => {
    setCartItems(prevItems => prevItems.filter(item => item._id !== productId));
  };

  const handleInitiateCheckout = () => {
    if (cartItems.length === 0) {
      toast.warn("Le panier est vide");
      return;
    }
    setIsCheckoutModalOpen(true);
  };

  const handleFinalizeSale = async ({ amountReceived, manualDiscount, loyaltyDiscount, paymentMethod, pointsUsed }) => {
    // Récupérer l'objet client complet pour l'affichage immédiat
    const clientObj = clients.find(c => c._id === selectedClientId);
    const totalDiscount = manualDiscount + loyaltyDiscount;
    const finalTotal = total - totalDiscount;
    
    const salePayload = {
        items: cartItems.map(item => ({ 
            productId: item.isMisc ? null : (item.originalProductId || item._id),
            variantName: item.variantName,
            quantity: item.quantity,
            unitPrice: item.isMisc ? item.salePrice : undefined,
            name: item.isMisc ? item.name : undefined
        })),
        isCreditSale: false,
        amountReceived: amountReceived,
        discount: totalDiscount,
        clientId: selectedClientId || undefined,
        loyaltyPointsUsed: pointsUsed || 0,
        paymentMethod: paymentMethod || 'Espèces',
        date: new Date().toISOString()
    };

    if (!isOnline) {
        const queue = JSON.parse(localStorage.getItem('offline_sales_queue') || '[]');
        queue.push(salePayload);
        localStorage.setItem('offline_sales_queue', JSON.stringify(queue));
        
        toast.warning("Pas de connexion. Vente sauvegardée localement 💾");
        
        // Simulation de la réussite pour l'interface
        const mockSale = { 
            ...salePayload, 
            _id: `OFFLINE-${Date.now()}`, 
            totalAmount: total, 
            items: cartItems,
            clientId: clientObj // Important : on passe l'objet client entier
        };
        setCompletedSale(mockSale);
        setCartItems([]);
        setSelectedClientId('');
        setClientSearchTerm('');
        setIsCheckoutModalOpen(false);
        setIsReceiptModalOpen(true);
        return;
    }

    try {
      console.log("Envoi de la vente :", salePayload); // Pour débogage
      const response = await api.post('/sales', salePayload);
      
      // On s'assure que l'objet vente contient bien les infos client pour l'affichage
      // et on met à jour localement les points pour un retour immédiat
      const saleData = response.data;
      if (clientObj) {
          const pointsEarned = Math.floor(finalTotal / 1000);
          const currentPoints = clientObj.loyaltyPoints || 0;
          const updatedClient = {
              ...clientObj,
              loyaltyPoints: currentPoints - (pointsUsed || 0) + pointsEarned,
          };
          saleData.clientId = updatedClient;
      }

      // Le toast affiche le montant final après remise
      toast.success(`Vente de ${finalTotal.toLocaleString('fr-FR')} CFA validée !`);
      setCompletedSale(saleData); 
      setCartItems([]);
      setSelectedClientId(''); // Reset client
      setClientSearchTerm('');
      setIsCheckoutModalOpen(false);
      setIsReceiptModalOpen(true); // Ouvrir le modal du ticket
      refreshDashboard(); // On rafraîchit les données du dashboard !
    } catch (error) {
      toast.error("Une erreur est survenue lors de la validation de la vente.");
      console.error(error);
    }
  };

  const handleCreditSale = async ({ clientId: creditClientId, dueDate }) => {
    if (cartItems.length === 0) {
      toast.warn("Le panier est vide");
    return;
    }
    
    const clientObj = clients.find(c => c._id === creditClientId);

    const salePayload = {
        items: cartItems.map(item => ({ 
            productId: item.isMisc ? null : (item.originalProductId || item._id),
            variantName: item.variantName,
            quantity: item.quantity,
            unitPrice: item.isMisc ? item.salePrice : undefined,
            name: item.isMisc ? item.name : undefined
        })),
        isCreditSale: true,
        clientId: creditClientId,
        dueDate: dueDate,
        sellerId: user._id,
        date: new Date().toISOString()
    };

    if (!isOnline) {
        const queue = JSON.parse(localStorage.getItem('offline_sales_queue') || '[]');
        queue.push(salePayload);
        localStorage.setItem('offline_sales_queue', JSON.stringify(queue));
        
        toast.warning("Pas de connexion. Dette sauvegardée localement 💾");
        
        const mockSale = { 
            ...salePayload, 
            _id: `OFFLINE-${Date.now()}`, 
            totalAmount: total, 
            items: cartItems,
            clientId: clientObj
        };
        setCompletedSale(mockSale);
        setCartItems([]);
        setSelectedClientId('');
        setClientSearchTerm('');
        setIsCreditModalOpen(false);
        setIsReceiptModalOpen(true);
        return;
    }

    try {
      const response = await api.post('/sales', salePayload);
      
      const saleData = response.data;
      if (clientObj && (!saleData.clientId || typeof saleData.clientId !== 'object')) {
          saleData.clientId = clientObj;
      }

      toast.success(`Dette de ${total.toLocaleString('fr-FR')} CFA ajoutée pour le client !`);
      setCompletedSale(saleData); 
      setCartItems([]);
      setSelectedClientId('');
      setClientSearchTerm('');
      setIsCreditModalOpen(false);
      setIsReceiptModalOpen(true); // Ouvrir le modal du ticket
      refreshDashboard(); // On rafraîchit aussi le dashboard
    } catch (error) {
      toast.error("Une erreur est survenue lors de la vente à crédit.");
      console.error(error);
    }
  };

  const handleHoldSale = () => {
    if (cartItems.length === 0) {
      toast.warn("Le panier est vide.");
      return;
    }
    const newHeldSale = { id: Date.now(), items: cartItems, total };
    setHeldSales(prev => [...prev, newHeldSale]);
    setCartItems([]);
    toast.info("Vente mise en attente.");
  };

  const handleResumeSale = (saleId) => {
    if (cartItems.length > 0 && !window.confirm("Le panier actuel sera écrasé. Continuer ?")) {
      return;
    }
    const saleToResume = heldSales.find(s => s.id === saleId);
    setCartItems(saleToResume.items);
    setHeldSales(prev => prev.filter(s => s.id !== saleId));
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchTerm.trim() !== '') {
      e.preventDefault(); // Empêche le comportement par défaut du formulaire

      // Recherche par code-barres en priorité
      const productByBarcode = allProducts.find(p => p.barcode === searchTerm.trim());
      if (productByBarcode) {
        handleAddToCart(productByBarcode);
        setSearchTerm(''); // Réinitialise le champ après l'ajout
        return;
      }

      // Si pas de code-barres, on cherche le premier produit correspondant au nom
      if (filteredProducts.length > 0) {
        handleAddToCart(filteredProducts[0]);
        setSearchTerm('');
      } else {
        // Si aucun produit n'est trouvé (ni par code-barres, ni par nom)
        toast.error(`Aucun produit trouvé pour "${searchTerm}".`);
      }
    }
  };

  const filteredProducts = useMemo(() => {
    if (!searchTerm) return allProducts;
    
    const lowerSearch = normalizeString(searchTerm.trim());
    // Liste enrichie de mots vides pour ignorer le bruit (ex: "je veux du lait")
    const stopWords = ['le', 'la', 'les', 'un', 'une', 'des', 'du', 'de', 'et', 'ou', 'au', 'aux', 'a', 'je', 'veux', 'donne', 'moi', 'pour'];
    
    // Découpe la recherche en mots, retire les mots vides (stop words)
    let searchTokens = lowerSearch.split(/\s+/).filter(t => !stopWords.includes(t) && t.length > 0);
    
    // Si la recherche ne contient que des stop words, on utilise la recherche brute
    if (searchTokens.length === 0 && lowerSearch.length > 0) searchTokens = lowerSearch.split(/\s+/);

    return allProducts.filter(product => {
      const productName = normalizeString(product.name);
      const barcode = product.barcode ? normalizeString(product.barcode) : '';
      const productWords = productName.split(/\s+/);

      if (barcode.includes(lowerSearch)) return true;

      return searchTokens.every(token => {
        if (productName.includes(token)) return true;
        // Recherche floue (tolérance aux fautes/phonétique)
        return productWords.some(word => {
            const dist = levenshteinDistance(token, word);
            // Amélioration : Tolérance basée sur la longueur (approx 30% d'erreur acceptée)
            // Ex: "Vitalait" (8 chars) -> accepte 2-3 erreurs (comme "Vitales")
            // Ex: "Lait" (4 chars) -> accepte 1 erreur
            const maxErrors = Math.floor(word.length * 0.4); 
            return dist <= maxErrors;
        });
      });
    });
  }, [allProducts, searchTerm]);

  const pageCount = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  const handleShareReceipt = () => {
    if (!completedSale) return;
    
    const shopName = user?.shopName || 'Ma Boutique';
    let message = `*Reçu - ${shopName}* 🛒\n`;
    message += `📅 ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}\n`;
    message += `------------------------------\n`;
    
    completedSale.items.forEach(item => {
        // Gestion robuste si item.name n'est pas directement disponible (dépend de l'API)
        const name = item.name || allProducts.find(p => p._id === item.productId)?.name || 'Article';
        message += `${item.quantity}x ${name} : ${(item.unitPrice || 0).toLocaleString()} F\n`;
    });
    
    message += `------------------------------\n`;
    message += `*TOTAL : ${(completedSale.totalAmount || total).toLocaleString()} FCFA*\n\n`;
    message += `Merci de votre visite ! 🙏`;

    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    productGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Vérifie si le magasin est vide (pas seulement la recherche, mais tout le stock)
  const isStoreEmpty = allProducts.length === 0 && !loading;

  if (loading) {
    return <Spinner message="Chargement de la caisse..." />;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
      {/* Colonne des produits */}
      <div id="tour-step-sales-products" ref={productGridRef} className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        {!isOnline && (
            <div className="mb-4 bg-orange-50 border-l-4 border-orange-500 text-orange-700 p-4 rounded shadow-sm flex items-center">
                <FiWifiOff className="mr-3" size={24} />
                <div>
                    <p className="font-bold">Mode Hors-Ligne activé</p>
                    <p className="text-sm">Vous pouvez continuer à vendre. Vos données seront synchronisées automatiquement dès le retour de la connexion.</p>
                </div>
            </div>
        )}
        <div className="relative mb-4">
          <FiSearch className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Rechercher ou scanner un code-barres..."
            className="w-full bg-gray-50 border-transparent focus:bg-white border focus:border-gray-300 rounded-xl py-3 pl-10 pr-20 focus:ring-0 outline-none transition-all text-sm font-medium"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          <div className="absolute top-1/2 right-3 -translate-y-1/2 flex items-center gap-1">
            <button onClick={() => setIsScannerOpen(true)} className="p-2 rounded-full text-gray-400 hover:text-brand-blue hover:bg-gray-100 transition-colors" title="Scanner avec la caméra">
                <FiCamera size={20} />
            </button>
            <button onClick={handleVoiceSearch} className={`p-2 rounded-full transition-colors ${isListening ? 'bg-red-100 text-red-600 animate-pulse' : 'text-gray-400 hover:text-brand-blue'}`} title="Recherche vocale">
                <FiMic size={20} />
            </button>
          </div>
        </div>
        {topProducts.length > 0 && searchTerm === '' && (
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2 items-center">
              <h4 className="text-sm font-bold text-gray-600 mr-2 flex items-center"><FiStar className="mr-1 text-yellow-500"/>Rapide:</h4>
              {topProducts.map(product => (
                <button key={product._id} onClick={() => handleAddToCart(product)} className="bg-blue-100 text-brand-blue text-sm font-semibold px-3 py-1 rounded-full hover:bg-blue-200 transition-colors">
                  {product.name}
                </button>
              ))}
            </div>
            <button onClick={() => setIsMiscModalOpen(true)} className="bg-gray-100 text-gray-700 text-sm font-bold px-3 py-1.5 rounded-full hover:bg-gray-200 transition-colors flex items-center whitespace-nowrap">
                <FiTag className="mr-2" /> Vente Libre
            </button>
          </div>
        )}
        <div className="flex flex-col flex-grow">
          {isStoreEmpty ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 min-h-[300px]">
                <div className="w-20 h-20 bg-blue-50 text-brand-blue rounded-full flex items-center justify-center mb-4">
                    <FiPackage size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Votre magasin est vide</h3>
                <p className="text-gray-500 mb-6 max-w-sm">
                    Pour commencer à vendre, vous devez d'abord ajouter vos produits ou services au stock.
                </p>
                <Link to="/dashboard/stock" className="bg-brand-blue text-white font-bold py-3 px-6 rounded-xl hover:bg-blue-700 transition-colors shadow-lg flex items-center">
                    <FiPlusCircle className="mr-2" />
                    Ajouter mon premier produit
                </Link>
            </div>
          ) : (
            <div
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 overflow-y-auto flex-grow"
                style={{maxHeight: `calc(100vh - 300px)`}}
            >
                {paginatedProducts.length > 0 ? paginatedProducts.map(product => (
                <div key={product._id} onClick={() => handleAddToCart(product)} className="bg-white border border-gray-100 rounded-xl p-4 flex flex-col items-center justify-between text-center cursor-pointer hover:shadow-lg hover:border-brand-blue hover:-translate-y-1 transition-all duration-200 h-full group">
                    {product.images && product.images.length > 0 ? (
                    <img src={product.images[0]} alt={product.name} className="w-12 h-12 rounded-full object-cover mb-3 border border-gray-100" />
                    ) : (
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold mb-3 ${getProductColor(product.name)}`}>
                        {product.name.substring(0, 2).toUpperCase()}
                    </div>
                    )}
                    <p className="font-bold text-gray-800 text-sm line-clamp-2 leading-tight mb-1">{product.name}</p>
                    <p className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{product.salePrice.toLocaleString('fr-FR')} F</p>
                    <button className="mt-3 text-gray-300 group-hover:text-brand-green transition-colors" aria-label={`Ajouter ${product.name} au panier`}>
                    <FiPlusCircle className="w-8 h-8" />
                    </button>
                </div>
                )) : (
                <p className="col-span-full text-center text-gray-500 mt-8">Aucun produit trouvé correspondant à votre recherche.</p>
                )}
            </div>
          )}

          {pageCount > 1 && searchTerm !== '' && (
            <div className="pt-4 flex justify-center items-center">
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-l disabled:opacity-50">
                  <FiChevronLeft/>
              </button>
              <span className="text-sm px-4">Page {currentPage} / {pageCount}</span>
              <button
                onClick={() => handlePageChange(Math.min(pageCount, currentPage + 1))}
                disabled={currentPage === pageCount}
                className="text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-r disabled:opacity-50">
                  <FiChevronRight/>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Colonne du panier */}
      <div id="tour-step-sales-cart" className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col">
        <div className="flex justify-between items-center border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold flex items-center"><FiShoppingCart className="mr-2"/>Vente en cours</h3>
          {heldSales.length > 0 && (
            <div className="relative group z-20">
              <button className="relative text-brand-blue p-1">
                <FiPause />
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">{heldSales.length}</span>
              </button>
              {/* Dropdown avec pont invisible (padding-top) pour éviter la fermeture lors du survol */}
              <div className="absolute right-0 top-full pt-2 w-64 hidden group-hover:block">
                <div className="bg-white border rounded-lg shadow-xl overflow-hidden">
                    <p className="p-2 text-xs font-bold border-b bg-gray-50 text-gray-700">Ventes en attente</p>
                    <div className="max-h-60 overflow-y-auto">
                        {heldSales.map(sale => (
                        <div key={sale.id} onClick={() => handleResumeSale(sale.id)} className="p-3 hover:bg-blue-50 cursor-pointer flex justify-between items-center text-sm border-b last:border-0 transition-colors">
                            <span className="font-medium text-gray-800">Total: {sale.total.toLocaleString('fr-FR')} F</span>
                            <FiPlay className="text-green-600"/>
                        </div>
                        ))}
                    </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sélecteur de Client (Fidélité) */}
        <div className="mb-4" ref={clientDropdownRef}>
          <div className="relative">
            <FiUser className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un client..."
              value={clientSearchTerm}
              onChange={(e) => {
                setClientSearchTerm(e.target.value);
                setIsClientDropdownOpen(true);
                if (selectedClientId) setSelectedClientId(''); // Désélectionner si on modifie
              }}
              onFocus={() => setIsClientDropdownOpen(true)}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl py-2.5 pl-10 pr-4 text-sm focus:outline-none focus:border-brand-blue"
            />
            {selectedClientId && (
                <button onClick={() => { setSelectedClientId(''); setClientSearchTerm(''); }} className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-red-500">
                    <FiXCircle />
                </button>
            )}
            
            {isClientDropdownOpen && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg mt-1 max-h-60 overflow-y-auto z-20">
                    <div 
                        className="p-3 hover:bg-gray-50 cursor-pointer text-sm text-gray-600 italic border-b"
                        onClick={() => { setSelectedClientId(''); setClientSearchTerm(''); setIsClientDropdownOpen(false); }}
                    >
                        Client de passage (Anonyme)
                    </div>
                    {clients.filter(c => c.name.toLowerCase().includes(clientSearchTerm.toLowerCase())).map(client => (
                        <div 
                            key={client._id} 
                            className="p-3 hover:bg-blue-50 cursor-pointer text-sm border-b last:border-0"
                            onClick={() => { setSelectedClientId(client._id); setClientSearchTerm(client.name); setIsClientDropdownOpen(false); }}
                        >
                            <p className="font-bold text-gray-800">{client.name}</p>
                            {client.phone && <p className="text-xs text-gray-500">{client.phone}</p>}
                        </div>
                    ))}
                    {clients.filter(c => c.name.toLowerCase().includes(clientSearchTerm.toLowerCase())).length === 0 && (
                        <div className="p-3 text-sm text-gray-500 text-center">Aucun client trouvé</div>
                    )}
                </div>
            )}
          </div>
        </div>

        <div className="flex-grow space-y-3 overflow-y-auto">
          {cartItems.length > 0 ? cartItems.map(item => (
            <div key={item._id} className="flex justify-between items-center">
              <div>
                <p className="font-semibold text-sm">{item.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <button onClick={() => updateQuantity(item._id, -1)} className="bg-gray-200 rounded-full p-1 hover:bg-gray-300 transition-colors" disabled={item.quantity <= 1}><FiMinus size={12} /></button>
                  <span className="font-bold text-sm">{item.quantity}</span>
                  <button onClick={() => updateQuantity(item._id, 1)} className="bg-gray-200 rounded-full p-1 hover:bg-gray-300 transition-colors disabled:opacity-50" disabled={!item.isMisc && !item.isService && item.quantity >= item.stock}>
                    <FiPlus size={12} />
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <p className="font-bold text-sm">{(item.quantity * item.salePrice).toLocaleString('fr-FR')} CFA</p>
                <button onClick={() => removeFromCart(item._id)} className="text-red-500 hover:text-red-700">
                  <FiXCircle />
                </button>
              </div>
            </div>
          )) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <p className="mb-4">Le panier est vide.</p>
              {completedSale && (
                <div className="flex flex-col gap-2 w-full px-8">
                  <button 
                    onClick={handleShareReceipt} 
                    className="flex items-center justify-center px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-md font-bold"
                  >
                    <FiShare2 className="mr-2" /> Envoyer Ticket WhatsApp
                  </button>
                  <button 
                    onClick={() => setIsReceiptModalOpen(true)} 
                    className="flex items-center justify-center px-4 py-2 border border-brand-blue text-brand-blue rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <FiPrinter className="mr-2" /> Réimprimer Ticket
                  </button>
                  <button 
                    onClick={() => setIsInvoiceModalOpen(true)} 
                    className="flex items-center justify-center px-4 py-2 bg-brand-blue text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <FiFileText className="mr-2" /> Générer Facture
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {crossSellSuggestions.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-dashed border-blue-300">
            <h4 className="text-xs font-bold text-blue-800 mb-2 flex items-center"><FiZap className="mr-1"/>Suggestions</h4>
            <div className="flex flex-wrap gap-2">
              {crossSellSuggestions.map(suggestion => (
                <button 
                  key={suggestion._id} 
                  onClick={() => addToCart(suggestion)} 
                  className="text-xs bg-white border border-blue-300 text-blue-800 px-2 py-1 rounded-full hover:bg-blue-100"
                >+ {suggestion.name}</button>
              ))}
            </div>
          </div>
        )}
        <div className="border-t mt-4 pt-4">
          <div className="flex justify-between items-center mb-4">
            <span className="text-lg font-bold">Total</span>
            <span className="text-2xl font-bold text-brand-green">{total.toLocaleString('fr-FR')} CFA</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => {
                if (user?.plan === 'essentiel') {
                  toast.info("Mettez des ventes en attente pour servir plusieurs clients à la fois. Passez au plan Pro pour activer cette fonctionnalité.", { icon: "👥" });
                  openUpgradeModal();
                } else {
                  handleHoldSale();
                }
              }}
              className={`w-full font-bold py-3 px-2 rounded-xl transition-colors text-sm flex items-center justify-center ${user?.plan === 'essentiel' ? 'bg-yellow-300 text-white cursor-not-allowed' : 'bg-yellow-500 hover:bg-yellow-600 text-white'}`}
              title="Mettre la vente en attente">
              <FiPause className="mr-1"/> Attente
            </button>
            <button 
              onClick={() => {
                if (user?.plan === 'essentiel') {
                  toast.info("Fidélisez vos clients en leur offrant des crédits. Passez au plan Pro pour activer la vente à crédit.", { icon: "💳" });
                  openUpgradeModal();
                } else {
                  setIsCreditModalOpen(true);
                }
              }}
              className={`w-full font-bold py-3 px-2 rounded-xl transition-colors text-sm flex items-center justify-center ${user?.plan === 'essentiel' ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
              title={user?.plan === 'essentiel' ? "Passez au plan Pro pour utiliser cette fonctionnalité" : ""}>
              Crédit
            </button>
            <button onClick={handleInitiateCheckout} className="w-full bg-brand-blue hover:bg-blue-700 text-white font-bold py-3 px-2 rounded-xl transition-colors text-sm flex items-center justify-center">
              Valider
            </button>
          </div>
        </div>
      </div>
      <ClientCreditModal isOpen={isCreditModalOpen} onClose={() => setIsCreditModalOpen(false)} onConfirm={handleCreditSale} clients={clients} />
      <CheckoutModal 
        isOpen={isCheckoutModalOpen} 
        onClose={() => setIsCheckoutModalOpen(false)} 
        total={total} 
        onConfirm={handleFinalizeSale} 
        client={clients.find(c => c._id === selectedClientId)}
      />
      <ReceiptModal 
        isOpen={isReceiptModalOpen} 
        onClose={() => setIsReceiptModalOpen(false)} 
        sale={completedSale} 
      />
      <InvoiceModal 
        isOpen={isInvoiceModalOpen} 
        onClose={() => setIsInvoiceModalOpen(false)} 
        sale={completedSale}
        user={user}
      />
      <MiscItemModal isOpen={isMiscModalOpen} onClose={() => setIsMiscModalOpen(false)} onConfirm={addMiscItemToCart} />
      <VariantSelectionModal 
        isOpen={isVariantModalOpen} 
        onClose={() => setIsVariantModalOpen(false)} 
        product={productForVariantSelection} 
        onSelectVariant={handleSelectVariant} 
      />
      <ScannerModal isOpen={isScannerOpen} onClose={() => setIsScannerOpen(false)} onScan={handleBarcodeScan} />
    </div>
  );
};

export default Sales;